import { UnscheduledSectionsQuery } from '../../unscheduled_sections/queries/unscheduled_sections_query';
import { ScheduledSectionsQuery } from '../../course_scheduler/queries/scheduled_sections_query';
import { SectionTime } from '../builders/section_time_builder';
import { CheckTimeWithInstructorUtility } from '../../../utilities/is_check_time_with_instructor_fn';
import { ISection, ISectionTime, IConflictMessage } from '../../../interfaces/scheduler_interfaces';
import * as _4 from 'lodash4';

export interface IFormattedSectionTime {
  times: string[];
}

interface ITimeAndDay {
  day: string;
  time: string;
}
export class SectionInfoServant {
  constructor(
    private ngCheckTimeWithInstructorUtility: CheckTimeWithInstructorUtility,
    private ngUnscheduledSectionsQuery: UnscheduledSectionsQuery,
    private ngScheduledSectionsQuery: ScheduledSectionsQuery,
    private schedulerConfigModel: any,
    private FEATURE_SECTION_LABEL_NK: string
  ) {
    const weekdaysShort = [' ', ...moment.weekdaysShort()];
    const weekdaysMin = [' ', ...moment.weekdaysMin()];
    const weekdays = [' ', ...moment.weekdays()];
    moment.locale('en', { weekdaysShort, weekdaysMin, weekdays });
  }
  sectionCourseTime(section: ISection): string {
    return this.convertDateToRange(section.class_start_dt, section.class_end_dt);
  }

  convertDate(dateString: string) {
    const date = moment(dateString, 'YYYY-MM-DD');
    return date.format('MMM Do YYYY');
  }

  convertDateToRange(
    startDate: string,
    endDate: string,
    startFormat: string = 'MMM Do',
    endFormat: string = 'MMM Do YYYY'
  ) {
    const classStart = moment(startDate, 'YYYY-MM-DD');
    const classEnd = moment(endDate, 'YYYY-MM-DD');

    const isYearDiff = classStart.year() !== classEnd.year();

    const formatStart = isYearDiff ? `${startFormat} YYYY` : startFormat;
    const formatEnd = isYearDiff ? `${endFormat} YYYY` : endFormat;

    const start = classStart.format(formatStart);
    const end = classEnd.format(formatEnd);
    // Jan 8th - Jul 16th 2016
    // Dec 23rd 2017 - Jan 31st 2018
    return `${start} - ${end}`;
  }

  calculateSectionTimes(section: ISection): IFormattedSectionTime[] {
    // Group Section times by Campus -- not needed, each section is associated with one campus.
    // Group Section Times By Location
    // For Each location - Campus Name | Location name
    // Format times
    // if check with instructor = "Check with instructor for times"
    // if weekday 8  = "Meeting times TBD"
    // format [from - to] times

    if (!section || this.ngCheckTimeWithInstructorUtility.areTimesNotSpecified(section._section_times)) {
      return [
        {
          times: ['Check with instructor for times']
        }
      ];
    }

    const sectionTimesByLocation = _4.groupBy(section._section_times, 'location');
    const formattedSectionTimesByLocation = _4.map(
      sectionTimesByLocation,
      (sectionTimes: ISectionTime[]): IFormattedSectionTime => {
        const daysAndTimes: ITimeAndDay[] = _4.map(
          _4.sortBy(sectionTimes, ['day_of_week']),
          (sectionTime: ISectionTime) => {
            const startTime = sectionTime.from_tm ? moment(sectionTime.from_tm, 'HH:mm:ss').format('h:mm a') : 'TBD';
            const endTime = sectionTime.to_tm ? moment(sectionTime.to_tm, 'HH:mm:ss').format('h:mm a') : 'TBD';
            const dayOfWeek =
              sectionTime.day_of_week === 8
                ? 'TBD'
                : sectionTime.day_of_week === 7
                ? 'Sat'
                : moment().weekday(sectionTime.day_of_week).format('ddd');
            return {
              day: dayOfWeek,
              time: `${startTime} - ${endTime}`
            };
          }
        );
        // group all section times by from and to time e.g. we want something that looks like:
        // Mon Wed 9 - 11
        // Wed 2 - 3
        const groupByTimes = _4.groupBy(daysAndTimes, 'time');
        const times: string[] = _4.map(groupByTimes, (timeGroup, key) => {
          const days = _4.reduce(timeGroup, (prev, time) => `${prev} ${time.day}`, '');
          return `${days} ${key}`;
        });
        return {
          times
        };
      }
    );

    return formattedSectionTimesByLocation;
  }
  getSectionLocationString(section: ISection) {
    const specificLocation = _4.uniq(
      _4.map(_4.get(section, '_section_times', []), (sectionTime: ISectionTime) => `${sectionTime._location.name}`)
    );
    const campus = this.getSectionCampus(section);
    return `${campus} ${specificLocation.length > 0 ? '|' : ''} ${specificLocation.join(' ')}`;
  }
  getSectionCampus(section: ISection) {
    if (!_4.get(section, '_campus.name')) {
      return '';
    }
    return section._campus.is_online ? `Online Campus` : section._campus.name ? `${section._campus.name}` : 'TBD';
  }
  getConflictingScheduledSections(conflictingScheduledSectionIds: number[]) {
    return this.getCourseTitlesFromScheduledSectionIds(conflictingScheduledSectionIds, false);
  }
  getConflictingLinkedSections(conflictingLinkedSectionIds: number[]) {
    return this.getCourseTitlesFromScheduledSectionIds(conflictingLinkedSectionIds, true);
  }

  getSectionLabel(section: ISection) {
    return `${this.schedulerConfigModel.getConfigProperty(this.FEATURE_SECTION_LABEL_NK)} ${section.cd}`;
  }

  getConflictingMessages(section: ISection) {
    const conflictingMessages: IConflictMessage[] = [];
    const sectionId = section.id;
    if (_4.includes(section._availability.conflicting_selected_section_ids, sectionId)) {
      return [
        ...conflictingMessages,
        {
          style: 'error',
          message: `Section scheduled`
        }
      ];
    }
    if (section._availability.is_conflicting_with_selected_section) {
      const conflictingCourses = this.getConflictingScheduledSections(
        section._availability.conflicting_selected_section_ids
      );
      conflictingMessages.push({
        style: 'error',
        message: `Conflicts with ${conflictingCourses.join(' ')}`
      });
    }
    if (section._availability.is_conflicting_with_linked_section) {
      const conflictingCourses = this.getConflictingLinkedSections(
        section._availability.conflicting_linked_section_ids
      );
      conflictingMessages.push({
        style: 'error',
        message: `Not offered with ${conflictingCourses.join(' ')}`
      });
    }

    if (section._availability.is_conflicting_with_student_campus) {
      conflictingMessages.push({
        style: 'warning',
        message: 'Conflicts with your campus preferences'
      });
    }

    if (section._availability.is_conflicting_with_student_time) {
      conflictingMessages.push({
        style: 'warning',
        message: 'Conflicts with your busy times'
      });
    }
    return conflictingMessages;
  }
  // Conflicts are always with scheduled sections. Conflicts only arise when a section is picked. Ignore others.
  private getCourseTitlesFromScheduledSectionIds(sectionIds: number[], isLinkedSections: boolean) {
    const sectionsFromSectionIds = _4.compact(
      _4.map(sectionIds, (sectionId: number) =>
        _4.find(this.ngScheduledSectionsQuery.result.list, (section: ISection) => section.id === sectionId)
      )
    );
    return _4.map(
      sectionsFromSectionIds,
      (section: ISection) =>
        `${section._course.cd} ${section._course.title}` + (isLinkedSections ? ` ${this.getSectionLabel(section)}` : '')
    );
  }
}
angular.module('capability-ng-scheduler').service('ngSectionInfoServant', SectionInfoServant);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/sections/servants/section_info_servant.ts